import { useCallback } from 'react'
import { buildPublicApiUrl } from '../config/api'

export const useOrders = () => {
  const apiUrl = buildPublicApiUrl('orders')

  const withAuth = useCallback(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Unauthorized')
    }

    return {
      Authorization: `${token}`,
      'Content-Type': 'application/json'
    }
  }, [])

  const fetchOrders = useCallback(async (filters = {}) => {
    const query = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value)
      }
    })

    const response = await fetch(`${apiUrl}${query.toString() ? `?${query.toString()}` : ''}`, {
      headers: withAuth()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch orders (${response.status})`)
    }
    return response.json()
  }, [apiUrl, withAuth])

  const getOrder = useCallback(async (orderId) => {
    const response = await fetch(`${apiUrl}/${orderId}`, {
      headers: withAuth()
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch order (${response.status})`)
    }
    return response.json()
  }, [apiUrl, withAuth])

  const uploadBankTransferProof = useCallback(async (orderId, file) => {
    const authHeaders = withAuth()
    const prepareResponse = await fetch(apiUrl, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        action: 'prepare_bank_transfer_proof',
        order_id: orderId,
        file_name: file.name,
        content_type: file.type,
        size: file.size
      })
    })
    const preparePayload = await prepareResponse.json()
    if (!prepareResponse.ok) {
      throw new Error(preparePayload.message || `Failed to prepare upload (${prepareResponse.status})`)
    }

    const uploadResponse = await fetch(preparePayload.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    })
    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload payment proof (${uploadResponse.status})`)
    }

    const completeResponse = await fetch(apiUrl, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        action: 'complete_bank_transfer_proof',
        order_id: orderId,
        storage_key: preparePayload.storage_key,
        original_filename: file.name,
        content_type: file.type,
        size: file.size
      })
    })
    const completePayload = await completeResponse.json()
    if (!completeResponse.ok) {
      throw new Error(completePayload.message || `Failed to finalize upload (${completeResponse.status})`)
    }
    return completePayload
  }, [apiUrl, withAuth])

  return { fetchOrders, getOrder, uploadBankTransferProof }
}
